import {inject, Injectable} from '@angular/core';
import {Router} from '@angular/router';
import {BehaviorSubject, Observable, throwError} from 'rxjs';
import {catchError, map, switchMap, tap} from 'rxjs/operators';
import {AuthApiService, LoginResponse, RefreshTokenResponse} from '@core/services/api/auth-api.service';
import {StorageService} from '@core/services/storage.service';
import {PermissionService} from '@core/services/permission.service';

@Injectable({
    providedIn: 'root'
})
export class AuthService {
    private authApiService = inject(AuthApiService);
    private storageService = inject(StorageService);
    private permissionService = inject(PermissionService);
    private router = inject(Router);

    private isRefreshing = false;
    private refreshTokenSubject: BehaviorSubject<string | null> = new BehaviorSubject<string | null>(null);

    login(email: string, password: string): Observable<LoginResponse> {
        return this.authApiService.login(email, password).pipe(
            tap(response => {
                if (response.success && response.data) {
                    this.setSession(response.data);
                }
            })
        );
    }

    refreshToken(): Observable<RefreshTokenResponse> {
        const refreshToken = this.storageService.refreshToken;

        if (!refreshToken) {
            this.logout();
            return throwError(() => new Error('No refresh token available'));
        }

        this.isRefreshing = true;
        this.refreshTokenSubject.next(null);

        return this.authApiService.refreshToken(refreshToken).pipe(
            tap(response => {
                if (response.success && response.data) {
                    this.storageService.token = response.data.accessToken;
                    this.storageService.refreshToken = response.data.refreshToken;
                    this.refreshTokenSubject.next(response.data.accessToken);
                }
                this.isRefreshing = false;
            }),
            catchError(err => {
                this.isRefreshing = false;
                this.logout();
                return throwError(() => err);
            })
        );
    }

    logout(): void {
        // Limpar o storage
        this.storageService.reset();

        // Limpar o perfil no PermissionService
        this.permissionService.updateProfile('');

        // Navegar para login
        this.router.navigate(['/login']);

    }

    isLoggedIn(): boolean {
        return this.storageService.isLogged();
    }

    getToken(): string {
        return this.storageService.token;
    }

    getRefreshToken(): string {
        return this.storageService.refreshToken;
    }

    isRefreshingToken(): boolean {
        return this.isRefreshing;
    }

    getRefreshTokenSubject(): Observable<string | null> {
        return this.refreshTokenSubject.asObservable();
    }

    private setSession(data: LoginResponse['data']): void {
        this.storageService.token = data.accessToken;
        this.storageService.refreshToken = data.refreshToken;
        this.storageService.email = data.user.email;
        this.storageService.firstName = data.user.firstName;
        this.storageService.lastName = data.user.lastName;
        this.storageService.profile = data.user.profile;

        // Atualizar o perfil no PermissionService
        this.permissionService.updateProfile(data.user.profile);
    }
}

